import { useState } from 'react';
import useSWR from 'swr';
import { api, getUserToken, getAdminToken } from '@utils/api';

/**
 * Hook to validate redeem code
 */
export const useValidateCode = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const validateCode = async (code) => {
        setLoading(true);
        setError(null);

        try {
            const token = getUserToken();
            if (!token) {
                throw new Error('Please login first');
            }

            const response = await fetch(api('/api/redeem/validate'), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ code })
            });

            const result = await response.json();

            if (!response.ok || !result.success) {
                throw new Error(result.error || result.message || 'Invalid code');
            }

            setLoading(false);
            return { success: true, data: result.data };
        } catch (err) {
            setError(err.message);
            setLoading(false);
            return { success: false, error: err.message };
        }
    };

    return { validateCode, loading, error };
};

/**
 * Hook to apply redeem code
 */
export const useApplyCode = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const applyCode = async (code) => {
        setLoading(true);
        setError(null);

        try {
            const token = getUserToken();
            if (!token) {
                throw new Error('Please login first');
            }

            const response = await fetch(api('/api/redeem/apply'), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ code })
            });

            const result = await response.json();

            if (!response.ok || !result.success) {
                throw new Error(result.error || result.message || 'Failed to apply code');
            }

            setLoading(false);
            return { success: true, data: result.data };
        } catch (err) {
            setError(err.message);
            setLoading(false);
            return { success: false, error: err.message };
        }
    };

    return { applyCode, loading, error };
};

/**
 * Hook to fetch redeem history
 */
export const useRedeemHistory = () => {
    const token = getUserToken();

    const { data, error, isLoading, mutate } = useSWR(
        token ? [api('/api/redeem/history'), token] : null,
        async ([url, token]) => {
            const response = await fetch(url, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                throw new Error('Failed to fetch redeem history');
            }

            const result = await response.json();
            return result.data || {};
        },
        {
            revalidateOnFocus: false,
            dedupingInterval: 60000 // 1 minute
        }
    );

    return {
        history: data?.history || [],
        total: data?.total || 0,
        isLoading,
        error,
        mutate
    };
};

/**
 * Admin hook to fetch all redeem codes
 */
export const useAdminRedeemCodes = () => {
    const token = getAdminToken(); // ✅ Fixed: Use admin token

    const { data, error, isLoading, mutate } = useSWR(
        token ? [api('/api/admin/redeem-codes'), token] : null,
        async ([url, token]) => {
            console.log('[useAdminRedeemCodes] Fetching from:', url);
            console.log('[useAdminRedeemCodes] Token exists:', !!token);
            
            const response = await fetch(url, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            console.log('[useAdminRedeemCodes] Response status:', response.status);

            if (!response.ok) {
                const errorText = await response.text();
                console.error('[useAdminRedeemCodes] Error response:', errorText);
                throw new Error(`Failed to fetch redeem codes: ${response.status}`);
            }

            const result = await response.json();
            console.log('[useAdminRedeemCodes] Result:', result);
            console.log('[useAdminRedeemCodes] Codes count:', result.data?.codes?.length || 0);
            
            return result.data || {};
        },
        {
            revalidateOnFocus: false,
            dedupingInterval: 30000, // 30 seconds
            onError: (err) => {
                console.error('[useAdminRedeemCodes] SWR Error:', err);
            }
        }
    );

    console.log('[useAdminRedeemCodes] Hook state:', { 
        codesLength: data?.codes?.length || 0, 
        isLoading, 
        hasError: !!error 
    });

    return {
        codes: data?.codes || [],
        total: data?.total || 0,
        isLoading,
        error,
        mutate
    };
};

/**
 * Admin hook to manage redeem codes
 */
export const useAdminRedeemActions = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const createCode = async (codeData) => {
        setLoading(true);
        setError(null);

        try {
            const token = getAdminToken(); // ✅ Fixed: Use admin token
            const response = await fetch(api('/api/admin/redeem-codes'), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(codeData)
            });

            const result = await response.json();

            if (!response.ok || !result.success) {
                throw new Error(result.message || 'Failed to create code');
            }

            setLoading(false);
            return { success: true, data: result.data };
        } catch (err) {
            setError(err.message);
            setLoading(false);
            return { success: false, error: err.message };
        }
    };

    const updateCode = async (id, updates) => {
        setLoading(true);
        setError(null);

        try {
            const token = getAdminToken(); // ✅ Fixed: Use admin token
            const response = await fetch(api(`/api/admin/redeem-codes/${id}`), {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(updates)
            });

            const result = await response.json();

            if (!response.ok || !result.success) {
                throw new Error(result.message || 'Failed to update code');
            }

            setLoading(false);
            return { success: true, data: result.data };
        } catch (err) {
            setError(err.message);
            setLoading(false);
            return { success: false, error: err.message };
        }
    };

    const deleteCode = async (id) => {
        setLoading(true);
        setError(null);

        try {
            const token = getAdminToken(); // ✅ Fixed: Use admin token
            const response = await fetch(api(`/api/admin/redeem-codes/${id}`), {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            const result = await response.json();

            if (!response.ok || !result.success) {
                throw new Error(result.message || 'Failed to delete code');
            }

            setLoading(false);
            return { success: true };
        } catch (err) {
            setError(err.message);
            setLoading(false);
            return { success: false, error: err.message };
        }
    };

    return { createCode, updateCode, deleteCode, loading, error };
};

/**
 * Admin hook to fetch redeem analytics
 */
export const useRedeemAnalytics = () => {
    const token = getAdminToken(); // ✅ Fixed: Use admin token

    const { data, error, isLoading, mutate } = useSWR(
        token ? [api('/api/admin/redeem-analytics'), token] : null,
        async ([url, token]) => {
            console.log('[useRedeemAnalytics] Fetching from:', url);
            
            const response = await fetch(url, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            console.log('[useRedeemAnalytics] Response status:', response.status);

            if (!response.ok) {
                const errorText = await response.text();
                console.error('[useRedeemAnalytics] Error response:', errorText);
                throw new Error(`Failed to fetch analytics: ${response.status}`);
            }

            const result = await response.json();
            console.log('[useRedeemAnalytics] Result:', result);
            
            return result.data?.analytics || {};
        },
        {
            revalidateOnFocus: false,
            dedupingInterval: 60000, // 1 minute
            onError: (err) => {
                console.error('[useRedeemAnalytics] SWR Error:', err);
            }
        }
    );

    return {
        analytics: data || {},
        isLoading,
        error,
        mutate
    };
};

export default useValidateCode;

